import { useState } from "react";
import { Divider, Grid, Icon, IconButton } from "@mui/material";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import StickyHeadTable from "ui-components/tables/StickyTable";
import TimelineList from "ui-components/Timeline/TimelineList";
import TimelineItem from "ui-components/Timeline/TimelineItem";
import Drawer from "ui-components/Drawer";
import { getSOLog } from "services/data.outbound.service";
import { soStatusLabel } from "layouts/views/common/enum";
import MDTypography from "components/MDTypography";
import { getSOStatusLog } from "services/data.outbound.service";
import { getCarrierList } from "services/data.internal.service";
import { SelectFields } from "ui-components/Form";
import MDButton from "components/MDButton";
import { editSOCarrier } from "services/data.outbound.service";
import { editSOOrderType } from "services/data.outbound.service";
import { LoadingDialog } from "ui-components/Alert";
import { Fieldset } from "ui-components/Form";
import { Texts } from "ui-components/Form";

const columns = [
  { label: "SKU Code", id: "skuCode" },
  { label: "Quantity", id: "quantity" },
  { label: "Base UOM", id: "baseUOM" },
  { label: "Cancel Quantity", id: "cancelQuantity" },
  { label: "Remark", id: "remark" },
  { label: "Cancel Date", id: "cancelDate" },
  { label: "Status", id: "status" },
];

function SOLog({ open, setOpen, logRequest, setLogRequest }) {
  const queryClient = useQueryClient();
  const [edit, setEdit] = useState(false);
  const [editInfo, setEditInfo] = useState({carrier:null,shipPostcode:null,shipCity:null,shipTelNo:null});
  const [editOrderType, setEditOrderType] = useState(false);
  const { data: so } = useQuery({
    queryKey: ["so-log", { logRequest }],
    queryFn: () => getSOLog(logRequest),
    enabled: Boolean(logRequest),
  });
  const { data: carrierData } = useQuery({
    queryKey: ["carrier-options"],
    queryFn: () => getCarrierList({ active: true }),
    enabled: Boolean(logRequest) && logRequest.status === "O",
  });

  const item = so?.data.data;

  const carriers = carrierData?.data.data.map((x) => x.carrierCode) ?? [];

  const { data } = useQuery({
    queryKey: ["so-status-log", { logRequest }],
    queryFn: () => getSOStatusLog(logRequest),
    enabled: logRequest !== null,
  });
  const details = data?.data.data.map(({ id, ...log }) => <TimelineItem key={id} {...log} />);

  const toggleDrawer = (open) => (event) => {
    if (event.type === "keydown" && (event.key === "Tab" || event.key === "Shift")) {
      return;
    }
    setOpen(open);
    setLogRequest(null);
  };
  const editMutation = useMutation(editSOCarrier, {
    onSuccess: () => {
      queryClient.invalidateQueries(["so-log"]);
      setEdit(false);
      setEditInfo({carrier:null,shipPostcode:null,shipCity:null,shipTelNo:null});
    },
  });
  const onSubmit = () => {
    editMutation.mutate({
      orderId:logRequest.orderId,
      ...editInfo,
    });
  };
  const editStatus = editMutation.status === "loading";

  const canEdit = edit && item?.shipmentType === "NonIntegrated"

  const orderTypeMutation = useMutation(editSOOrderType, {
    onSuccess: () => {
      queryClient.invalidateQueries(["so-log"]);
      setEditOrderType(false);
    },
  });
  const onSubmitOrderType = () => {
    orderTypeMutation.mutate({ orderId: logRequest.orderId });
  };
  const orderTypeStatus = orderTypeMutation.status === "loading";
  const canFixOrderType =
    Boolean(logRequest) &&
    (logRequest.status === "D" || logRequest.status === "L") &&
    item?.orderType !== "B2B" &&
    item?.carrier === "SNT";

  return (
    <Drawer width="1400px" open={open} onClose={toggleDrawer(false)} onOpen={toggleDrawer(true)}>
      {editStatus && <LoadingDialog />}
      {orderTypeStatus && <LoadingDialog />}
      <MDTypography variant="h5" color="info">
        Sales Order
      </MDTypography>
      <Divider sx={{ margin: "5px 0", background: "#9ebeeb", opacity: "unset" }} />
      {/* {logs} */}
      {Boolean(item) && (
        <>
          <div style={{ fontSize: "14px", marginBottom: "20px" }}>
            <Grid container>
              <Grid item xs={3}>
                <b>Customer Code : </b>
                {item.customerCode}
              </Grid>
              <Grid item xs={3}>
                <b>Customer Order Number : </b>
                {item.custOrdNbr}
              </Grid>
              <Grid item xs={3}>
                <b>Order Number : </b>
                {item.orderNumber}
              </Grid>

              <Grid item xs={3}>
                <b>Warehouse Code : </b>
                {item.warehouseCode}
              </Grid>
              <Grid item xs={3}>
                <b>Total Amount : </b>
                {item.currencyCode} {item.totalAmount}
              </Grid>
              <Grid item xs={3}>
                <b>Order Date : </b>
                {item.orderDate}
              </Grid>
              <Grid item xs={3}>
                <b>Ship Date : </b>
                {item.shipDate}
              </Grid>
              <Grid item xs={3}>
                <b>Order Type : </b>
                {item.orderType}
                {canFixOrderType && (
                  <IconButton
                    sx={{ width: "1rem", height: "1rem" }}
                    color="success"
                    onClick={() => {
                      setEditOrderType(true);
                    }}
                  >
                    <Icon>edit</Icon>
                  </IconButton>
                )}
              </Grid>
            </Grid>
            {editOrderType && canFixOrderType && (
              <Fieldset label="Fix Order Type">
                <Grid container spacing={1} alignItems="center">
                  {/* <Grid item>
                    This order was entered as {item.orderType}, but Carrier suggests it should be B2B.
                  </Grid> */}
                  <Grid item>
                    <MDButton color="info" onClick={onSubmitOrderType}>
                      Fix to B2B
                    </MDButton>
                  </Grid>
                  <Grid item>
                    <MDButton color="secondary" onClick={() => setEditOrderType(false)}>
                      Cancel
                    </MDButton>
                  </Grid>
                </Grid>
              </Fieldset>
            )}
            <MDTypography variant="h5" color="info">
              Shipping
            </MDTypography>
            <Divider sx={{ margin: "5px 0", background: "#9ebeeb", opacity: "unset" }} />
            <Grid container>
              <Grid item xs={3}>
                <b>Ship Company : </b>
                {item.shipCompany}
              </Grid>
              <Grid item xs={9}>
                <b>Ship Address 1 : </b>
                {item.shipAddress1}
              </Grid>
              <Grid item xs={3}>
                <b>Ship Contact : </b>
                {item.shipContact}
              </Grid>
              <Grid item xs={3}>
                <b>Ship Postcode : </b>
                {item.shipPostcode}
              </Grid>
              <Grid item xs={3}>
                <b>Ship City : </b>
                {item.shipCity}
              </Grid>
              <Grid item xs={3}>
                <b>Ship State : </b>
                {item.shipState}
              </Grid>
              <Grid item xs={3}>
                <b>Ship Phone :</b>
                {item.shipPhone}
              </Grid>
              <Grid item xs={3}>
                <b>Ship Country : </b>
                {item.shipCountry}
              </Grid>
            </Grid>
            <MDTypography variant="h5" color="info">
              Carrier{" "}
              {Boolean(logRequest) && logRequest.status === "O" && item?.shipmentType === "NonIntegrated" && (
                <IconButton
                  sx={{ width: "1rem", height: "1rem" }}
                  color="success"
                  onClick={() => {
                    setEdit(true);
                  }}
                >
                  <Icon>edit</Icon>
                </IconButton>
              )}
            </MDTypography>
            <Divider sx={{ margin: "5px 0", background: "#9ebeeb", opacity: "unset" }} />
            <Grid container>
              <Grid item xs={12 / 5}>
                <b>Shipping Zone : </b>
                {item.shippingZone}
              </Grid>
              <Grid item xs={12 / 5}>
                <b>Shipment Type : </b>
                {item.shipmentType}
              </Grid>
              <Grid item xs={12 / 5}>
                <b>Carrier : </b>
                {item.carrier}
              </Grid>
              <Grid item xs={12 / 5}>
                <b>Reference No : </b>
                {item.referenceNo}
              </Grid>
              <Grid item xs={12 / 5}>
                <b>Marketplace : </b>
                {item.salesRepName}
              </Grid>
            </Grid>
            {canEdit && (
              <>
                <Fieldset label="Edit">
                  <Grid container spacing={1}>
                    <Grid item xs={2}>
                      <SelectFields
                        field={{ name: "", label: "Carrier", value: editInfo?.carrier, errors: {} }}
                        options={carriers}
                        onChange={(e, value) => setEditInfo(prev=>({...prev,carrier:value}))}
                      />
                    </Grid>
                    <Grid item xs={2}>
                      <Texts
                        field={{ name: "", label: "Postcode", value: editInfo?.shipPostcode, errors: {} }}
                        onChange={(e) => setEditInfo(prev=>({...prev,shipPostcode:e.target.value}))}
                      />
                    </Grid>
                    <Grid item xs={2}>
                      <Texts
                        field={{ name: "", label: "City", value: editInfo?.shipCity, errors: {} }}
                        onChange={(e) => setEditInfo(prev=>({...prev,shipCity:e.target.value}))}
                      />
                    </Grid>
                    <Grid item xs={2}>
                      <Texts
                        field={{ name: "", label: "Phone", value: editInfo?.shipTelNo, errors: {} }}
                        onChange={(e) => setEditInfo(prev=>({...prev,shipTelNo:e.target.value}))}
                      />
                    </Grid>
                    <Grid sx={{ marginLeft: "1rem" }} item xs={3}>
                      <MDButton color="info" onClick={onSubmit} disabled={!Boolean(editInfo?.carrier) && !Boolean(editInfo?.shipPostcode) && !Boolean(editInfo?.shipCity) && !Boolean(editInfo?.shipTelNo)}>
                        Submit
                      </MDButton>
                    </Grid>
                  </Grid>
                </Fieldset>
                {/* <Divider sx={{ margin: "5px 0", background: "#9ebeeb", opacity: "unset" }} /> */}
              </>
            )}
          </div>
          <div>
            <StickyHeadTable
              columns={columns}
              rows={item.details.map((x) => ({ ...x, status: soStatusLabel[x.status] }))}
            />
          </div>
          <TimelineList title="Logs">
            {item.logs.map(({ id, ...log }) => (
              <TimelineItem key={id} {...log} />
            ))}
          </TimelineList>
        </>
      )}
      <Divider />
      {Boolean(details) && details.length > 0 && (
        <TimelineList title="Order Transaction Logs">{details}</TimelineList>
      )}
    </Drawer>
  );
}

export default SOLog;
